/*
{
  key: "message:123",
  context_type: "message",
  context_id: "123",
  ability: {
    can: ['forum:vote'], // subject:action
    cannot: [],
  },
  token: "789",
  children: []
}
*/

import moment from 'moment';
import { ref, reactive, provide, inject, watch, computed, toValue } from 'vue';
import { api } from '/app/plugins/services';
import { defineAbility } from '/app/lib/abilities';
import { MODERATION_ACTIONS } from '/app/constant/moderation-actions';
import { useAuth } from '/app/composables/auth';
import { usePlatform } from '/app/composables/platform';
import { singleFlight } from '/app/utils/single-flight';

// One permissions call is made per mounted ModerationContext. Sibling contexts
// with the same parents, actions and id build an identical payload, so without
// this they stampede the endpoint with copies of the same request.
const loadPermissions = singleFlight(
    (payload) => api.moderation.permissions(payload),
    (payload) => JSON.stringify(payload),
);

// Injection key for parent context
export const MODERATION_CONTEXT_KEY = Symbol('moderation-context');

// Injection key for user ban context (current user banned from platform)
export const USER_BAN_KEY = Symbol('user-ban');

/**
 * Inject the user ban context (provided by UserBanProvider).
 * Returns { isBanned, untilDate, loading, error } about the current user being banned from the platform.
 */
export const useUserBan = () => inject(USER_BAN_KEY, { isBanned: false, untilDate: null, loading: false, error: null });

// Enhanced composable for individual ModerationContext instances
export const useModerationContextInstance = (contextType, contextId, actions = [], options = {}) => {
    const { auth } = useAuth();
    const { contextPlatform } = usePlatform();
    // Try to inject parent context
    const { contextData: parentContext, abilities: parentAbilities } = inject(MODERATION_CONTEXT_KEY, {});

    const inherit = computed(() => toValue(options.inherit) || false);
    const abilities = computed(() => inherit.value && parentAbilities ? parentAbilities.value : ownAbilities.value);
    const ownAbilities = ref(defineAbility([]));

    // Reactive values that can handle refs, computed, or getter functions
    const currentContextType = computed(() => toValue(contextType));
    const currentContextId = computed(() => toValue(contextId));
    const currentActions = computed(() => toValue(actions) || []);
    
    // Create current context data with reactive properties
    const contextData = reactive({
        context_type: currentContextType.value,
        context_id: currentContextId.value,
        key: computed(() => `${currentContextType.value}:${currentContextId.value}`),
        parent: parentContext,
        children: new Set(),
        moderations: []
    });

    // Add this context as a child to parent if parent exists
    if (parentContext) {
        parentContext.children.add(contextData);
    }

    // Watch for parameter changes and update context data
    watch([currentContextType, currentContextId], ([newType, newId]) => {
        contextData.context_type = newType;
        contextData.context_id = newId;
    }, { immediate: false });

    const mutedUntil = computed(() => {
        const [moderation] = contextData?.moderations || [];

        return moderation?.until_date || moment().add(1, 'day').toDate();
    });

    // Helper functions for traversing the context tree
    const getParentContexts = () => {
        const parents = [];
        let current = parentContext;
        while (current) {
            parents.push(current);
            current = current.parent;
        }
        return parents;
    };

    const getRootContext = () => {
        let current = contextData;
        while (current.parent) {
            current = current.parent;
        }
        return current;
    };

    const getAllChildContexts = () => {
        const allChildren = [];
        const traverse = (context) => {
            context.children.forEach(child => {
                allChildren.push(child);
                traverse(child);
            });
        };
        traverse(contextData);
        return allChildren;
    };

    const cleanup = () => {
        // Remove from parent's children
        if (parentContext) {
            parentContext.children.delete(contextData);
        }
    };

    // Bumped on every run — including the ones that bail out below — so a
    // response that lands after a newer request cannot overwrite it. The watch
    // driving this fires in bursts as the context props settle.
    let generation = 0;

    const updateAbilities = async () => {
        const request = ++generation;

        // When inheriting from parent, skip the API call
        if (inherit.value) {
            return;
        }

        const currentType = currentContextType.value;
        const currentId = currentContextId.value;
        const currentActionsValue = currentActions.value;

        // Skip if we don't have required values
        if (!auth.authenticated || !currentType || !currentId || !currentActionsValue.length || !contextPlatform.value?.id) {
            return;
        }

        const contexts = getParentContexts();
        const contextArray = Array.from(contexts.values()).map(context => ({
            context_type: context.context_type,
            context_id: context.context_id,
        }));

        // Add current context to the array
        contextArray.push({
            context_type: currentType,
            context_id: currentId,
        });

        // Add current platform as context
        if (contextPlatform.value) {
            contextArray.push({
                context_type: 'platform',
                context_id: contextPlatform.value.id,
            });
        }

        const { data: response } = await loadPermissions({
            actions: currentActionsValue,
            contexts: contextArray,
            id: currentId,
        });

        if (request !== generation) {
            return;
        }

        const permission = response.data;

        // Map API dot-notation actions to frontend { subject, action, result }
        const normalizedActions = (permission?.actions ?? []).map(a => ({
            ...MODERATION_ACTIONS[a.action],
            result: a.result,
        }));

        ownAbilities.value = defineAbility(normalizedActions);
        contextData.moderations = permission?.moderations ?? null;
    }

    // Watch for changes in auth status and reactive parameters
    watch([
        () => auth.authenticated,
            currentContextType,
            currentContextId,
            currentActions,
            contextPlatform
        ], updateAbilities,
        { immediate: true },
    );

    // Provide this context to children
    provide(MODERATION_CONTEXT_KEY, {
        abilities,
        contextData,
        parentContext,
        mutedUntil,
        getParentContexts,
        getRootContext,
        getAllChildContexts,
        cleanup,
    });

    return {
        abilities,
        contextData,
        parentContext,
        mutedUntil,
        getParentContexts,
        getRootContext,
        getAllChildContexts,
        cleanup,
    };
};

// Global context manager (your existing shared composable enhanced)
export const useModerationContext = () => {
    const context = inject(MODERATION_CONTEXT_KEY, {});

    return context;
}